import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  DollarSign, Users, Building2, FileText, Download, TrendingUp,
  CheckCircle, CreditCard, Eye,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtNum = (v) => new Intl.NumberFormat('en-US').format(v || 0);

const COLORS = ['#3b82f6', '#16a34a', '#8b5cf6', '#f59e0b', '#ef4444', '#0ea5e9', '#ec4899', '#14b8a6'];

/* ============================================================
   CUSTOM TOOLTIP
   ============================================================ */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      fontSize: 12,
    }}>
      <p style={{ margin: 0, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '3px 0', color: p.color, fontWeight: 600 }}>
          {p.name}: {p.name.toLowerCase().includes('revenue') || p.name.toLowerCase().includes('paid') || p.name.toLowerCase().includes('pending') ? fmt(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

/* ============================================================
   PIE CHART LEGEND (CUSTOM)
   ============================================================ */
function PieLegend({ payload }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', justifyContent: 'center', marginTop: 8 }}>
      {payload?.map((entry, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569' }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: entry.color }} />
          <span>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   REPORTS PAGE
   ============================================================ */
export default function SuperAdminReports() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async () => {
    try {
      const r = await subscriptionsAPI.reports({ period });
      setData(r);
    } catch { toast.error('Failed to load reports'); }
  }, [period]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const handleExportCSV = () => {
    if (!data?.company_breakdown) return;
    const rows = [['Company', 'Plan', 'Status', 'Billing', 'Total Paid', 'Total Pending', 'Invoices', 'Period End']];
    data.company_breakdown.forEach(c => {
      rows.push([c.name, c.plan_name || 'None', c.sub_status || 'none', c.billing_cycle || '', c.total_paid || 0, c.total_pending || 0, c.invoice_count || 0, c.current_period_end || '']);
    });
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ledgerpro-reports-${period}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('Report exported!');
  };

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  const s = data?.summary || {};
  const timeline = data?.revenue_timeline || [];
  const plans = data?.revenue_by_plan || [];
  const companies = (data?.company_breakdown || []).filter(c => c.name);

  // Prepare pie chart data (plan distribution by revenue)
  const planPieData = plans.filter(p => p.total_revenue > 0).map(p => ({
    name: p.plan_name,
    value: parseFloat(p.total_revenue) || 0,
    subscribers: p.subscriber_count,
  }));

  // Prepare status pie data
  const activeCount = s.active_companies || 0;
  const totalCompanies = s.total_companies || 0;
  const noSubCount = totalCompanies - companies.filter(c => c.sub_status).length;
  const statusPieData = [
    { name: 'Active', value: companies.filter(c => c.sub_status === 'active').length },
    { name: 'Trialing', value: companies.filter(c => c.sub_status === 'trialing').length },
    { name: 'Pending', value: companies.filter(c => c.sub_status === 'pending').length },
    { name: 'Cancelled', value: companies.filter(c => c.sub_status === 'cancelled').length },
    { name: 'Expired', value: companies.filter(c => c.sub_status === 'expired').length },
    { name: 'Suspended', value: companies.filter(c => c.sub_status === 'suspended').length },
    { name: 'No Plan', value: noSubCount },
  ].filter(d => d.value > 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Reports & Analytics</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Revenue, payments, and company performance overview</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="sa-input sa-select" value={period} onChange={e => setPeriod(e.target.value)} style={{ width: 150, fontSize: 12, padding: '6px 10px' }}>
            <option value="daily">Last 30 Days</option>
            <option value="weekly">Last 12 Weeks</option>
            <option value="monthly">Last 12 Months</option>
            <option value="yearly">Last 5 Years</option>
          </select>
          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={handleExportCSV}>
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="sa-stats-grid" style={{ marginBottom: 24 }}>
        {[
          { icon: DollarSign, label: 'Total Revenue', value: fmt(s.total_revenue), color: '#16a34a' },
          { icon: DollarSign, label: 'Pending Revenue', value: fmt(s.pending_revenue), color: '#f59e0b' },
          { icon: TrendingUp, label: 'MRR', value: fmt(s.mrr), color: '#3b82f6' },
          { icon: TrendingUp, label: 'ARR', value: fmt(s.arr), color: '#8b5cf6' },
          { icon: Building2, label: 'Total Companies', value: fmtNum(s.total_companies), color: '#0ea5e9' },
          { icon: CheckCircle, label: 'Active Companies', value: fmtNum(s.active_companies), color: '#16a34a' },
          { icon: Users, label: 'Total Users', value: fmtNum(s.total_users), color: '#7c3aed' },
          { icon: FileText, label: 'Total Invoices', value: fmtNum(s.total_invoices), color: '#64748b' },
        ].map((item, i) => (
          <div key={i} className="sa-stat-card">
            <div className="sa-stat-icon" style={{ background: item.color }}><item.icon size={18} /></div>
            <div className="sa-stat-body">
              <div className="sa-stat-label">{item.label}</div>
              <div className="sa-stat-value" style={{ fontSize: 18 }}>{item.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="sa-filter-tabs" style={{ marginBottom: 20 }}>
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'timeline', label: 'Revenue Trend' },
          { key: 'plans', label: 'By Plan' },
          { key: 'companies', label: 'By Company' },
        ].map(t => (
          <button key={t.key} className={`sa-filter-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === 'overview' && (
        <div>
          {/* Two charts side by side */}
          <div className="sa-two-col" style={{ marginBottom: 20 }}>
            {/* Revenue Line Chart */}
            <div className="sa-card">
              <div className="sa-card-header">
                <h3 className="sa-card-title">Revenue Trend</h3>
                <span style={{ fontSize: 11, color: '#64748b' }}>Paid vs Pending</span>
              </div>
              <div className="sa-card-body" style={{ height: 280 }}>
                {timeline.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No timeline data</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={timeline} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend content={<PieLegend />} />
                      <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#16a34a" fill="url(#colorRevenue)" strokeWidth={2.5} dot={{ r: 4, fill: '#16a34a' }} activeDot={{ r: 6 }} />
                      <Area type="monotone" dataKey="pending" name="Pending" stroke="#f59e0b" fill="url(#colorPending)" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#f59e0b' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Subscription Status Pie */}
            <div className="sa-card">
              <div className="sa-card-header">
                <h3 className="sa-card-title">Subscription Status</h3>
              </div>
              <div className="sa-card-body" style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {statusPieData.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx="50%"
                        cy="45%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        stroke="none"
                      >
                        {statusPieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend content={<PieLegend />} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Bottom: Plan Revenue Bar + Plan Pie */}
          <div className="sa-two-col">
            {/* Plan Revenue Bar */}
            <div className="sa-card">
              <div className="sa-card-header">
                <h3 className="sa-card-title">Revenue by Plan</h3>
              </div>
              <div className="sa-card-body" style={{ height: 260 }}>
                {plans.filter(p => p.total_revenue > 0).length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No revenue data</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={plans.filter(p => p.total_revenue > 0)} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="plan_name" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total_revenue" name="Total Revenue" radius={[6, 6, 0, 0]} maxBarSize={60}>
                        {plans.filter(p => p.total_revenue > 0).map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Plan Distribution Pie */}
            <div className="sa-card">
              <div className="sa-card-header">
                <h3 className="sa-card-title">Revenue Share by Plan</h3>
              </div>
              <div className="sa-card-body" style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {planPieData.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={planPieData}
                        cx="50%"
                        cy="45%"
                        innerRadius={50}
                        outerRadius={85}
                        paddingAngle={3}
                        dataKey="value"
                        stroke="none"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                      >
                        {planPieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== TIMELINE TAB ==================== */}
      {activeTab === 'timeline' && (
        <div>
          {/* Main Line Chart */}
          <div className="sa-card" style={{ marginBottom: 20 }}>
            <div className="sa-card-header">
              <h3 className="sa-card-title">Revenue Over Time</h3>
              <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>● Revenue</span>
                <span style={{ color: '#f59e0b', fontWeight: 600 }}>● Pending</span>
                <span style={{ color: '#3b82f6', fontWeight: 600 }}>● Invoices</span>
              </div>
            </div>
            <div className="sa-card-body" style={{ height: 350 }}>
              {timeline.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No timeline data</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeline} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="revenue" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                    <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={<PieLegend />} />
                    <Line yAxisId="revenue" type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#16a34a" strokeWidth={3} dot={{ r: 5, fill: '#16a34a', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                    <Line yAxisId="revenue" type="monotone" dataKey="pending" name="Pending ($)" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 4, fill: '#f59e0b' }} />
                    <Line yAxisId="count" type="monotone" dataKey="invoice_count" name="Invoices" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6' }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Secondary: Stacked bar for paid vs pending */}
          <div className="sa-card" style={{ marginBottom: 20 }}>
            <div className="sa-card-header">
              <h3 className="sa-card-title">Paid vs Pending by Period</h3>
            </div>
            <div className="sa-card-body" style={{ height: 280 }}>
              {timeline.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No data</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={timeline} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={<PieLegend />} />
                    <Bar dataKey="paid_count" name="Paid Invoices" fill="#16a34a" radius={[4, 4, 0, 0]} stackId="stack" maxBarSize={50} />
                    <Bar dataKey="pending_count" name="Pending Invoices" fill="#f59e0b" radius={[4, 4, 0, 0]} stackId="stack" maxBarSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="sa-card">
            <div className="sa-card-header">
              <h3 className="sa-card-title">Detailed Timeline</h3>
            </div>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Revenue</th>
                    <th>Invoices</th>
                    <th>Paid</th>
                    <th>Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.map((t, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{t.period}</td>
                      <td style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(t.revenue)}</td>
                      <td>{t.invoice_count}</td>
                      <td style={{ color: '#16a34a' }}>{t.paid_count}</td>
                      <td style={{ color: '#f59e0b' }}>{t.pending_count}</td>
                    </tr>
                  ))}
                  {timeline.length === 0 && (
                    <tr><td colSpan={5} className="sa-empty-row">No timeline data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PLANS TAB ==================== */}
      {activeTab === 'plans' && (
        <div>
          {/* Plan charts row */}
          <div className="sa-two-col" style={{ marginBottom: 20 }}>
            {/* Plan Bar Chart */}
            <div className="sa-card">
              <div className="sa-card-header">
                <h3 className="sa-card-title">Revenue by Plan</h3>
              </div>
              <div className="sa-card-body" style={{ height: 280 }}>
                {plans.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No plan data</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={plans} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="plan_name" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend content={<PieLegend />} />
                      <Bar dataKey="total_revenue" name="Total Revenue" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={50} />
                      <Bar dataKey="pending_amount" name="Pending" fill="#f59e0b" radius={[6, 6, 0, 0]} maxBarSize={50} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Subscribers by Plan Pie */}
            <div className="sa-card">
              <div className="sa-card-header">
                <h3 className="sa-card-title">Subscribers by Plan</h3>
              </div>
              <div className="sa-card-body" style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {planPieData.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={planPieData.map(p => ({ name: p.name, value: p.subscribers }))}
                        cx="50%"
                        cy="45%"
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        stroke="none"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {planPieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend content={<PieLegend />} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Plan Details Table */}
          <div className="sa-card">
            <div className="sa-card-header">
              <h3 className="sa-card-title">Plan Details</h3>
            </div>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Monthly Price</th>
                    <th>Yearly Price</th>
                    <th>Subscribers</th>
                    <th>Total Revenue</th>
                    <th>Pending Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i % COLORS.length] }} />
                          <strong style={{ color: '#0f172a' }}>{p.plan_name}</strong>
                        </div>
                      </td>
                      <td>{fmt(p.price_monthly)}/mo</td>
                      <td>{fmt(p.price_yearly)}/yr</td>
                      <td><span className="sa-badge-status sa-badge-active">{p.subscriber_count}</span></td>
                      <td style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(p.total_revenue)}</td>
                      <td style={{ color: '#f59e0b' }}>{fmt(p.pending_amount)}</td>
                    </tr>
                  ))}
                  {plans.length === 0 && (
                    <tr><td colSpan={6} className="sa-empty-row">No plan data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== COMPANIES TAB ==================== */}
      {activeTab === 'companies' && (
        <div>
          {/* Company revenue bar chart */}
          <div className="sa-card" style={{ marginBottom: 20 }}>
            <div className="sa-card-header">
              <h3 className="sa-card-title">Company Revenue</h3>
            </div>
            <div className="sa-card-body" style={{ height: 300 }}>
              {companies.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>No company data</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={companies.filter(c => c.total_paid > 0)} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} tickLine={false} axisLine={false} width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={<PieLegend />} />
                    <Bar dataKey="total_paid" name="Paid" fill="#16a34a" radius={[0, 6, 6, 0]} maxBarSize={24} />
                    <Bar dataKey="total_pending" name="Pending" fill="#f59e0b" radius={[0, 6, 6, 0]} maxBarSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Company table */}
          <div className="sa-card">
            <div className="sa-card-header">
              <h3 className="sa-card-title">Company Revenue Breakdown</h3>
              <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={handleExportCSV}>
                <Download size={12} /> Export
              </button>
            </div>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Billing</th>
                    <th>Total Paid</th>
                    <th>Pending</th>
                    <th>Invoices</th>
                    <th>Period End</th>
                    <th className="sa-text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <div className="sa-company-cell">
                          <strong>{c.name}</strong>
                          <span>{c.email}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>{c.plan_name || 'No Plan'}</td>
                      <td>
                        <span className={`sa-badge-status sa-badge-${c.sub_status || 'expired'}`}>
                          {c.sub_status || 'none'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{c.billing_cycle || '—'}</td>
                      <td style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(c.total_paid)}</td>
                      <td style={{ color: '#f59e0b' }}>{fmt(c.total_pending)}</td>
                      <td>{c.invoice_count || 0}</td>
                      <td style={{ fontSize: 11 }}>{fmtDate(c.current_period_end)}</td>
                      <td className="sa-text-right">
                        <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate(`/super-admin/companies/${c.id}`)}>
                          <Eye size={12} /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {companies.length === 0 && (
                    <tr><td colSpan={9} className="sa-empty-row">No company data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
